import React from "react";
import { useLazyFetchLiveSubscriptionsQuery } from "../../../../services/graph";
import { LiveGraphSubscriptionType } from "../../../../types/GraphSubscriptionType";

export type LiveGraphSubscriptionsContextProps = {
    clear: () => void,
    data: LiveGraphSubscriptionType[],
    error: Error | string | null,
    hasMore: boolean,
    isFetching: boolean,
    isLoading: boolean,
    isNextPageLoading: boolean,
    isRowLoaded: boolean,
    loadMore: () => void,
};

export type LiveGraphSubscriptionsProviderProps = {
    children: React.ReactNode
};

const LiveGraphSubscriptionsContext = React.createContext<LiveGraphSubscriptionsContextProps>({
    clear: () => {},
    data: [],
    error: null,
    hasMore: false,
    isFetching: false,
    isLoading: false,
    isNextPageLoading: false,
    isRowLoaded: false,
    loadMore: () => {},
});

export const useLiveGraphSubscriptions = () => React.useContext(LiveGraphSubscriptionsContext);

export const LiveGraphSubscriptionsProvider = ({
    children
}: Readonly<LiveGraphSubscriptionsProviderProps>) => {
    const [isClearing, setIsClearing] = React.useState(false);
    const [cacheKey, setCacheKey] = React.useState(new Date().valueOf());
    const [isLoadingState, setIsLoadingState] = React.useState(false);
    const [load, { data, isLoading, isFetching, ...loadResults }] = useLazyFetchLiveSubscriptionsQuery();

    // extract the results of the last load to our report search
    // note: we need to have a default set of data in-case we have an undefined response
    const { nextLink, results }: CursorPagedResults<LiveGraphSubscriptionType> = data || { nextLink: null, results: [] };
    const hasMore = nextLink !== null;
    const isNextPageLoading = isFetching === true || isLoading === true || isLoadingState === true;

    const clear = () => {
        if (isNextPageLoading) {
            return;
        }

        const newCacheKey = new Date().valueOf();

        setIsLoadingState(_ => true);
        setIsClearing(_ => true);
        load({ cacheKey: newCacheKey, nextLink: null }).unwrap().then(_ => {
            setCacheKey(_prev => newCacheKey);
        }).finally(_ => {
            setIsLoadingState(_prev => false);
            setIsClearing(_ => false);
        });
    };

    const isRowLoaded = (index) => {
        return !hasMore || index < results.length;
    };

    const loadMore = () => {
        if (isNextPageLoading || !hasMore) {
            return;
        }

        setIsLoadingState(_ => true);
        load({ cacheKey, nextLink }).unwrap().then(_ => {
            setIsLoadingState(_prev => false);
        });
    };

    React.useEffect(clear, []);

    return (
        <LiveGraphSubscriptionsContext.Provider value={{
            ...loadResults,
            clear,
            data: isClearing ? [] : results,
            hasMore,
            isFetching,
            isLoading,
            isNextPageLoading,
            isRowLoaded,
            loadMore: isNextPageLoading ? () => {} : loadMore
        }}>
            {children}
        </LiveGraphSubscriptionsContext.Provider>
    );
};